import React, { useState, useRef } from 'react';
import {
  Drawer,
  Upload,
  Button,
  Badge,
  Image,
  message,
  Space,
  Tooltip,
  Modal,
  Typography,
  Row,
  Col,
  Tag
} from 'antd';
import {
  UploadOutlined,
  CloudOutlined,
  CameraOutlined,
  DeleteOutlined,
  EyeOutlined,
  DragOutlined
} from '@ant-design/icons';
import InspectionBtn from '@/components/InspectionBtn';
import toast from "react-hot-toast";

const { Title, Text } = Typography;

const StepImageGalleryDrawer = ({
  visible,
  onClose,
  gallery = {},
  modules = [],
  onUpload,
  onCameraCapture,
  onDeleteImage,
  isImageAssigned,
  currentStepName = null,
  stepIndex = null
}) => {
  const [cameraModalVisible, setCameraModalVisible] = useState(false);
  const [uploadingModule, setUploadingModule] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Get the current step's gallery, fallback to general if no current step
  const getCurrentStepKey = () => {
    if (currentStepName && modules.find(m => m.name === currentStepName)) {
      return currentStepName;
    }
    return 'general';
  };

  const getCurrentStepTitle = () => {
    if (currentStepName && modules.find(m => m.name === currentStepName)) {
      return currentStepName;
    }
    return 'General Images';
  };

  // Get module gallery
  const getModuleGallery = (moduleKey) => {
    return gallery[moduleKey] || [];
  };

  // Handle file upload with proper module targeting
  const handleUpload = async ({ fileList }, targetModule = null) => {
    const moduleToUse = targetModule || getCurrentStepKey();

    try {
      setUploadingModule(moduleToUse);
      const actualFiles = fileList.map(fileItem => {
        return fileItem.originFileObj || fileItem;
      }).filter(file => file instanceof File);

      if (actualFiles.length === 0) {
        toast.error('No valid files selected');
        return;
      }

      await onUpload(actualFiles, moduleToUse);

      const moduleDisplayName = moduleToUse === 'general' ? 'General Gallery' : moduleToUse;
      toast.success(`${actualFiles.length} images uploaded to ${moduleDisplayName}`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Failed to process images: " + error.message);
    } finally {
      setUploadingModule(null);
    }
  };

  // Camera functions
  const startCamera = async (targetModule = null) => {
    const moduleToUse = targetModule || getCurrentStepKey();

    try {
      setUploadingModule(moduleToUse);
      setCameraModalVisible(true);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Failed to access camera');
      setCameraModalVisible(false);
      setUploadingModule(null);
    }
  };

  const capturePhoto = async () => {
    try {
      if (!videoRef.current || !canvasRef.current) return;

      const canvas = canvasRef.current;
      const video = videoRef.current;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      canvas.toBlob(async (blob) => {
        if (blob) {
          const file = new File([blob], `camera_${Date.now()}.jpg`, { type: 'image/jpeg' });
          await onCameraCapture(file, uploadingModule);

          const moduleDisplayName = uploadingModule === 'general' ? 'General Gallery' : uploadingModule;
          toast.success(`Photo captured and added to ${moduleDisplayName}`);
        }
      }, 'image/jpeg', 0.8);

      stopCamera();
    } catch (error) {
      console.error('Capture error:', error);
      toast.error('Failed to capture photo');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraModalVisible(false);
    setUploadingModule(null);
  };

  // Fixed drag start handler
  const handleDragStart = (e, image, moduleKey) => {

    // Set drag effect
    e.dataTransfer.effectAllowed = "move";

    // Create comprehensive drag data with proper structure
    const dragData = {
      imageId: image.id,
      sourceModule: moduleKey,
      imageData: {
        id: image.id,
        url: image.url,
        name: image.name,
        uploaded: image.uploaded || true
      }
    };


    try {
      e.dataTransfer.setData('application/json', JSON.stringify(dragData));
      e.dataTransfer.setData('text/plain', JSON.stringify(dragData));

    } catch (error) {
      console.error('❌ StepDrawer: Error setting drag data:', error);
      toast.error('Failed to prepare image for dragging');
      return false;
    }

  };

  // Render gallery item with improved drag functionality
  const renderGalleryItem = (image, moduleKey) => {
    const isAssigned = isImageAssigned ? isImageAssigned(image.id) : false;

    return (
      <Col xs={24}
        sm={24}
        key={image.id}
        draggable={true}
        onDragStart={(e) =>
          handleDragStart(e, image, moduleKey)
        }
        className="gallery-item"
        style={{

          margin: '8px',
          border: isAssigned ? '2px solid #52c41a' : '2px solid #d9d9d9',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: isAssigned ? '#f6ffed' : '#fff',
          opacity: isAssigned ? 0.7 : 1,
          cursor: 'grab',
          transition: 'all 0.3s ease',
          boxShadow: isAssigned ? '0 2px 8px rgba(82, 196, 26, 0.2)' : '0 2px 4px rgba(0,0,0,0.1)',
          pointerEvents: 'auto'
        }
        }

      >
        {/* Image */}
        < Image
          src={image.url}
          alt={image.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          preview={{
            mask: (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                fontSize: '12px'
              }}>
                <EyeOutlined style={{ fontSize: '16px', marginBottom: '4px' }} />
                Preview
              </div>
            )
          }}
        />


        <div style={{
          position: 'absolute',
          top: '4px',
          left: '4px',
          backgroundColor: 'rgba(24, 144, 255, 0.9)',
          color: 'white',
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          cursor: 'grab'
        }}>
          <DragOutlined style={{ fontSize: '8px' }} />
          Drag
        </div>



        {/* Delete button - always clickable */}
        <Tooltip title="Delete image">
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDeleteImage(image.id, moduleKey);
            }}
            style={{
              position: 'absolute',
              bottom: '4px',
              right: '4px',
              backgroundColor: 'rgba(255,255,255,0.9)',
              border: 'none',
              width: '24px',
              height: '24px',
              minWidth: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10
            }}
          />
        </Tooltip>

        {/* Image name */}
        <div style={{
          position: 'absolute',
          bottom: '0',
          left: '0',
          right: '28px',
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: '2px 6px',
          fontSize: '10px',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        }}>
          {image.name}
        </div>
      </Col >
    );
  };

  // Render module gallery
  const renderModuleGallery = (moduleKey, moduleTitle, showUploadButtons = true) => {
    const moduleImages = getModuleGallery(moduleKey);
    const isUploading = uploadingModule === moduleKey;

    return (
      <div>
        {/* Upload buttons for the module */}
        {showUploadButtons && (
          <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>

            <Col>
              <Space>
                <Upload
                  multiple
                  showUploadList={false}
                  beforeUpload={() => false}
                  onChange={(info) => handleUpload(info, moduleKey)}
                  accept="image/*"
                  disabled={isUploading}
                >
                  <InspectionBtn
                    icon={<UploadOutlined />}
                    loading={isUploading}
                    type="primary"
                    size="small"
                  >
                    Upload
                  </InspectionBtn>
                </Upload>
                <InspectionBtn
                  icon={<CameraOutlined />}
                  onClick={() => startCamera(moduleKey)}
                  disabled={isUploading}
                  type="primary"
                  size="small"
                >
                  Camera
                </InspectionBtn>
              </Space>
            </Col>
          </Row>
        )}

        {/* Images grid */}
        {moduleImages.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#999',
            backgroundColor: '#fafafa',
            borderRadius: '12px',
            border: '2px dashed #d9d9d9'
          }}>
            <CloudOutlined style={{ fontSize: '48px', marginBottom: '16px', color: '#bfbfbf' }} />
            <div style={{ fontSize: '16px', marginBottom: '8px', fontWeight: '500' }}>
              No images for {moduleTitle}
            </div>
            <div style={{ fontSize: '14px', color: '#8c8c8c' }}>
              Upload images or capture photos to document this step
            </div>
          </div>
        ) : (
          <Row gutter={[16, 16]}>
            {moduleImages.map(image => renderGalleryItem(image, moduleKey))}
          </Row>
        )}

      </div>
    );
  };

  const currentStepKey = getCurrentStepKey();
  const currentStepTitle = getCurrentStepTitle();

  return (
    <>
      <Drawer
        mask={false}
        width={"250px"}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

            <Tag color="blue" style={{ margin: 0 }}>
              {currentStepTitle}   <Badge size='small'
              count={getModuleGallery(currentStepKey).length}
              showZero
             />
            </Tag>
         
            Images
          </div>
        }
        placement="right"
        open={visible}
        onClose={onClose}
        destroyOnClose={false}
      >
        <div>
          {renderModuleGallery(currentStepKey, currentStepTitle, true)}
        </div>


      </Drawer>

      {/* Camera Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CameraOutlined />
            Capture Photo for {uploadingModule === 'general' ? 'General Gallery' : uploadingModule}
          </div>
        }
        open={cameraModalVisible}
        onCancel={stopCamera}
        footer={[
          <InspectionBtn variant='secondary' key="cancel" onClick={stopCamera}>
            Cancel
          </InspectionBtn>,
          <InspectionBtn key="capture" type="primary" onClick={capturePhoto} icon={<CameraOutlined />}>
            Capture Photo
          </InspectionBtn>
        ]}
        width={650}
        destroyOnClose
      >
        <div style={{ textAlign: 'center' }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{
              width: '100%',
              maxWidth: '500px',
              height: 'auto',
              borderRadius: '8px',
              backgroundColor: '#000',
              border: '2px solid #d9d9d9'
            }}
          />
          <canvas
            ref={canvasRef}
            style={{ display: 'none' }}
          />
          <div style={{ marginTop: '16px' }}>
            <Text type="secondary">
              Capturing for: <Tag color="blue">{uploadingModule === 'general' ? 'General Gallery' : uploadingModule}</Tag>
            </Text>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default StepImageGalleryDrawer;